import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import Iconify from '../Iconify';

// ----------------------------------------------------------------------

const BENEFITS = [
    {
        icon: 'eva:eye-outline',
        title: 'Monitor All Reviews',
        description: 'View and track all your Google reviews in one centralized dashboard with real-time updates.',
    },
    {
        icon: 'eva:message-square-outline',
        title: 'Quick Responses',
        description: 'Respond to patient reviews instantly with pre-written templates and custom messages.',
    },
    {
        icon: 'eva:link-2-outline',
        title: 'Link to Patient Records',
        description: 'Connect reviews to patient profiles for better insights and personalized follow-ups.',
    },
    {
        icon: 'eva:paper-plane-outline',
        title: 'Request Reviews',
        description: 'Send automated review requests via email, SMS, or WhatsApp to satisfied patients.',
    },
    {
        icon: 'eva:bar-chart-outline',
        title: 'Analytics & Insights',
        description: 'Track response rates, average ratings, and review trends with detailed analytics.',
    },
    {
        icon: 'eva:people-outline',
        title: 'Multi-Location Support',
        description: 'Manage reviews across multiple practice locations from a single dashboard.',
    },
];

// Google colored dots SVG — replicating the reference HTML design
function GoogleDotsIcon({ size = 60 }) {
    return (
        <Box
            component="svg"
            viewBox="0 0 192 192"
            sx={{ width: size, height: size }}
        >
            <rect fill="#4285f4" height="58" rx="29" width="58" x="67" y="15" />
            <rect fill="#34a853" height="58" rx="29" width="58" x="119" y="67" />
            <rect fill="#fbbc04" height="58" rx="29" width="58" x="67" y="119" />
            <rect fill="#ea4335" height="58" rx="29" width="58" x="15" y="67" />
        </Box>
    );
}

GoogleDotsIcon.propTypes = {
    size: PropTypes.number,
};

// ----------------------------------------------------------------------

export default function GoogleReviewsEmptyState({ onConnect, onLearnMore }) {
    return (
        <Box
            sx={{
                maxWidth: 900,
                mx: 'auto',
                py: 7.5,
                px: 2,
            }}
        >
            <Box
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    boxShadow: (theme) =>
                        `0px 0px 2px 0px ${alpha(theme.palette.grey[500], 0.24)}, 0px 12px 24px -4px ${alpha(theme.palette.grey[500], 0.24)}`,
                    p: { xs: 5, md: 7.5 },
                    textAlign: 'center',
                }}
            >
                {/* Icon */}
                <Box sx={{ position: 'relative', display: 'inline-flex', mb: 4 }}>
                    {/* Outer glow ring */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 140,
                            height: 140,
                            borderRadius: '50%',
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            zIndex: 0,
                        }}
                    />
                    {/* Icon circle */}
                    <Box
                        sx={{
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            background: (theme) =>
                                `linear-gradient(135deg, ${theme.palette.primary.lighter} 0%, ${theme.palette.primary.light} 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <GoogleDotsIcon size={60} />
                    </Box>
                </Box>

                {/* Title */}
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}
                >
                    Connect Your Google Business Profile
                </Typography>

                {/* Description */}
                <Typography
                    variant="body1"
                    sx={{
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        maxWidth: 600,
                        mx: 'auto',
                        mb: 5,
                    }}
                >
                    Start managing your online reputation by connecting your Google Business Profile.
                    Monitor reviews, respond to patients, and boost your practice&apos;s
                    visibility&mdash;all from one place.
                </Typography>

                {/* CTA Buttons */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ mb: 3 }}
                >
                    <Button
                        size="large"
                        variant="contained"
                        onClick={onConnect}
                        startIcon={
                            <Box
                                component="svg"
                                viewBox="0 0 192 192"
                                sx={{ width: 24, height: 24, flexShrink: 0 }}
                            >
                                <rect fill="white" height="58" rx="29" width="58" x="67" y="15" />
                                <rect fill="white" height="58" rx="29" width="58" x="119" y="67" />
                                <rect fill="white" height="58" rx="29" width="58" x="67" y="119" />
                                <rect fill="white" height="58" rx="29" width="58" x="15" y="67" />
                            </Box>
                        }
                        sx={{
                            px: 6,
                            py: 2.5,
                            fontSize: 17,
                            fontWeight: 600,
                            borderRadius: 1.5,
                            background: (theme) =>
                                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: (theme) =>
                                `0 8px 24px ${alpha(theme.palette.primary.main, 0.32)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-3px)',
                                boxShadow: (theme) =>
                                    `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                                background: (theme) =>
                                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            },
                            '&:active': {
                                transform: 'translateY(-1px)',
                            },
                        }}
                    >
                        Connect Google Business Profile
                    </Button>

                    <Button
                        size="large"
                        variant="outlined"
                        onClick={onLearnMore}
                        startIcon={<Iconify icon="eva:info-outline" width={20} height={20} />}
                        sx={{
                            px: 4,
                            py: 2.5,
                            fontSize: 15,
                            fontWeight: 500,
                            borderRadius: 1.5,
                            borderColor: 'grey.300',
                            color: 'text.primary',
                            '&:hover': {
                                bgcolor: 'grey.100',
                                borderColor: 'grey.400',
                            },
                        }}
                    >
                        Learn More
                    </Button>
                </Stack>

                {/* Help text */}
                <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled', display: 'block', mb: 6 }}
                >
                    Need help?{' '}
                    <Box
                        component="span"
                        onClick={onConnect}
                        sx={{
                            color: 'primary.main',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        Contact support
                    </Box>
                    {' '}or{' '}
                    <Box
                        component="span"
                        onClick={onLearnMore}
                        sx={{
                            color: 'primary.main',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        view our documentation
                    </Box>
                </Typography>

                {/* Section Divider */}
                <Divider sx={{ mb: 5 }}>
                    <Typography
                        variant="overline"
                        sx={{
                            px: 3,
                            fontSize: 13,
                            fontWeight: 600,
                            color: 'text.secondary',
                            letterSpacing: '0.5px',
                        }}
                    >
                        What you&apos;ll get
                    </Typography>
                </Divider>

                {/* Benefits Grid */}
                <Grid container spacing={4} sx={{ mb: 6, textAlign: 'left' }}>
                    {BENEFITS.map((benefit) => (
                        <Grid item xs={12} sm={6} md={4} key={benefit.title}>
                            <Stack spacing={1.5}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 1.5,
                                        bgcolor: 'grey.100',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'primary.main',
                                    }}
                                >
                                    <Iconify icon={benefit.icon} width={24} height={24} />
                                </Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {benefit.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                    {benefit.description}
                                </Typography>
                            </Stack>
                        </Grid>
                    ))}
                </Grid>

                {/* Info Banner */}
                <Box
                    sx={{
                        bgcolor: (theme) => alpha(theme.palette.info.lighter, 1),
                        border: (theme) => `1px solid ${theme.palette.info.light}`,
                        borderRadius: 1.5,
                        p: '20px 24px',
                        display: 'flex',
                        gap: 2,
                        alignItems: 'flex-start',
                        textAlign: 'left',
                    }}
                >
                    <Iconify
                        icon="eva:info-outline"
                        width={24}
                        height={24}
                        sx={{ color: 'info.dark', flexShrink: 0, mt: 0.25 }}
                    />
                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={{ color: 'info.darker', mb: 0.5 }}
                        >
                            Your data is secure
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: 'info.dark', lineHeight: 1.6, display: 'block' }}
                        >
                            We use secure OAuth authentication and never store your Google credentials.
                            You can disconnect your account at any time from settings.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

GoogleReviewsEmptyState.propTypes = {
    onConnect: PropTypes.func,
    onLearnMore: PropTypes.func,
};

GoogleReviewsEmptyState.defaultProps = {
    onConnect: () => {},
    onLearnMore: () => {},
};
